import React, { useState, useCallback } from 'react';
import { Coins } from 'lucide-react';
import { motion } from 'framer-motion';
import { useGameCalculations } from '../../hooks/useGameCalculations';

interface ClickButtonProps {
  onClick: () => void;
}

export function ClickButton({ onClick }: ClickButtonProps) {
  const [isClicking, setIsClicking] = useState(false);
  const [particles, setParticles] = useState<{ id: number; x: number; y: number }[]>([]);
  const { calculateClickGains } = useGameCalculations(null);

  const handleClick = useCallback((event: React.MouseEvent) => {
    setIsClicking(true);
    
    const { isCritical } = calculateClickGains();
    
    // Create particles
    const rect = (event.target as HTMLElement).getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;

    setParticles(prev => [
      ...prev,
      { id: Date.now(), x, y }
    ]);

    onClick();
    setTimeout(() => setIsClicking(false), 100);
  }, [onClick, calculateClickGains]);

  // Clean up particles after animation
  const removeParticle = (id: number) => {
    setParticles(prev => prev.filter(p => p.id !== id));
  };

  return (
    <div className="flex justify-center mb-8">
      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        className={`
          relative w-48 h-48 
          bg-gradient-to-br from-yellow-400 to-yellow-600 
          rounded-full shadow-lg hover:shadow-yellow-500/50
          overflow-hidden
          ${isClicking ? 'shadow-inner' : ''}
        `}
        onClick={handleClick}
        style={{
          willChange: 'transform',
          transform: 'translateZ(0)',
          backfaceVisibility: 'hidden'
        }}
      >
        <div className="absolute inset-0 bg-white/20 rounded-full transform scale-0 animate-ripple" />
        <motion.div
          animate={isClicking ? { scale: 0.9 } : { scale: 1 }}
          transition={{ type: "spring", stiffness: 500, damping: 30 }}
        >
          <Coins size={64} className="mx-auto" />
        </motion.div>

        {particles.map(particle => (
          <motion.div
            key={particle.id}
            className="absolute w-2 h-2 bg-yellow-300 rounded-full"
            initial={{ x: particle.x, y: particle.y, opacity: 1 }}
            animate={{
              x: particle.x + (Math.random() - 0.5) * 100,
              y: particle.y - 100,
              opacity: 0
            }}
            transition={{ duration: 0.5 }}
            onAnimationComplete={() => removeParticle(particle.id)}
            style={{
              willChange: 'transform, opacity',
              transform: 'translateZ(0)',
              backfaceVisibility: 'hidden'
            }}
          />
        ))}
      </motion.button>
    </div>
  );
}